/* eslint-disable camelcase */
import { isUndefined, omitBy } from 'lodash';

import { ScimIntegrationStatus } from 'src/commons/api/v2/company';
import { AccountStage, BillingStage } from '../commons/api/v1/company';
import { EmploymentMetaData } from '../employment/store/types';
import { Role } from '../employment/types';
import { ClientSize } from '../analytics/helpers/mapClientSizeToLTV';

import readMetaTagObject from './readMetaTagObject';

export const EMPLOYMENT_META_DATA_TAG = 'x-employment-meta-data';

export type XEmploymentMetaData = {
  account_stage: AccountStage;
  billing_stage: BillingStage;
  client_id: number;
  client_created_at?: string;
  company_created_at?: string;
  company_has_address: boolean;
  company_id: number;
  company_name: string;
  system_start_date?: string;
  company_uuid: string;
  company_has_any_active_accounting_contract: boolean;
  company_has_any_adjustment_report_shared: boolean;
  company_has_non_admin_employees: boolean;
  company_old_excel_reports_and_hour_bank_page_enabled: boolean;
  client_size?: ClientSize;
  employment_id: number;
  employment_role: Role;
  first_attached_user_id: number;
  has_accepted_terms: boolean;
  has_custom_report: boolean;
  marketing_event_sent: boolean;
  override_feature_timetracking_excel_reports: boolean;
  user_cpf: string;
  user_email: string;
  user_created_at: string;
  user_first_name: string;
  user_hash: string;
  user_has_active_accountant: boolean;
  user_id: number;
  user_phone_number: string;
  won_probability: number;
  medical_certificates_limit?: number;
  managers_can_change_plan?: boolean;
  medical_certificates_specialized_review_enabled?: boolean;
  user_has_employments?: boolean;
  scim_integration_status?: ScimIntegrationStatus;
  client_industry?: string;
  should_reset_password?: boolean;
  email_verified: boolean;
  phone_number_verified: boolean;
  show_seconds_in_reports?: boolean;
  self_manager_adjustment_timetracking_blocked?: boolean;
};

export default function getEmploymentMetaData() {
  const {
    account_stage: accountStage,
    billing_stage: billingStage,
    client_id: clientId,
    client_created_at: clientCreatedAt,
    company_created_at: companyCreatedAt,
    company_has_address: companyHasAddress,
    company_id: companyId,
    company_name: companyName,
    system_start_date: systemStartDate,
    company_uuid: companyUuid,
    company_has_any_active_accounting_contract: companyHasAnyActiveAccountingContract,
    company_has_any_adjustment_report_shared: companyHasAnyAdjustmentReportShared,
    company_has_non_admin_employees: companyHasNonAdminEmployees,
    company_old_excel_reports_and_hour_bank_page_enabled: companyOldExcelReportsAndHourBankPageEnabled,
    client_size: clientSize,
    employment_id: employmentId,
    employment_role: employmentRole,
    first_attached_user_id: firstAttachedUserId,
    has_accepted_terms: hasAcceptedTerms,
    has_custom_report: hasCustomReport,
    marketing_event_sent: marketingEventSent,
    override_feature_timetracking_excel_reports: overrideFeatureTimetrackingExcelReports,
    user_cpf: userCpf,
    user_email: userEmail,
    user_first_name: userFirstName,
    user_created_at: userCreatedAt,
    user_hash: userHash,
    user_has_active_accountant: userHasActiveAccountant,
    user_id: userId,
    user_phone_number: userPhoneNumber,
    won_probability: wonProbability,
    medical_certificates_limit: medicalCertificatesLimit,
    managers_can_change_plan: managersCanChangePlan,
    medical_certificates_specialized_review_enabled: medicalCertificatesSpecializedReviewEnabled,
    user_has_employments: userHasEmployments,
    scim_integration_status: scimIntegrationStatus,
    client_industry: clientIndustry,
    should_reset_password: shouldResetPassword,
    email_verified: emailVerified,
    phone_number_verified: phoneNumberVerified,
    show_seconds_in_reports: showSecondsInReports,
    self_manager_adjustment_timetracking_blocked: selfManagerAdjustmentTimetrackingBlocked,
  }: XEmploymentMetaData = readMetaTagObject({
    tagName: EMPLOYMENT_META_DATA_TAG,
  });

  const metaData = {
    accountStage,
    billingStage,
    clientId,
    clientCreatedAt,
    companyCreatedAt,
    companyHasAddress,
    companyId,
    companyName,
    systemStartDate,
    companyUuid,
    companyHasAnyActiveAccountingContract,
    companyHasAnyAdjustmentReportShared,
    companyHasNonAdminEmployees,
    companyOldExcelReportsAndHourBankPageEnabled,
    clientSize,
    employmentId,
    employmentRole,
    firstAttachedUserId,
    hasAcceptedTerms,
    hasCustomReport,
    marketingEventSent,
    overrideFeatureTimetrackingExcelReports,
    userCpf,
    userEmail,
    userCreatedAt,
    userFirstName,
    userHash,
    userHasActiveAccountant,
    userId,
    userPhoneNumber,
    wonProbability,
    medicalCertificatesLimit,
    managersCanChangePlan,
    medicalCertificatesSpecializedReviewEnabled,
    userHasEmployments,
    scimIntegrationStatus,
    clientIndustry,
    shouldResetPassword,
    emailVerified,
    phoneNumberVerified,
    showSecondsInReports,
    selfManagerAdjustmentTimetrackingBlocked,
  };

  return omitBy(metaData, isUndefined) as EmploymentMetaData;
}
